import { makeStyles } from "tss-react/mui";

import { actionPrimary, controlSurface } from "@/shared/styles/action.styles";

const styles = makeStyles()((theme) => ({
  body: {
    "& .MuiDialog-paper": {
      backgroundColor: theme.design.color.surface.raised,
      color: theme.design.color.text.primary,
      display: "flex",
      flexDirection: "column",
      borderRadius: theme.design.radius.xl,
      fontFamily: theme.design.typography.fontFamily,
      fontWeight: 700,
      gap: theme.design.space.sm,
      overflow: "visible",
      border: `1px solid ${theme.design.color.border.popover}`,
      boxShadow: theme.design.shadow.lg,
      minWidth: 380,

      [theme.breakpoints.down("sm")]: {
        width: "100%",
        minWidth: "auto",
        margin: theme.design.space.md,
      },
    },
  },

  modalContent: {
    position: "relative",
  },

  contentBody: {
    display: "flex",
    flexDirection: "column",
    gap: theme.design.space.md,
    padding: "28px 24px 24px",
    alignItems: "center",

    [theme.breakpoints.down("sm")]: {
      padding: "28px 18px 20px",
    },
  },

  closeIconBody: {
    display: "flex",
    justifyContent: "flex-end",
    position: "absolute",
    right: 16,
    top: 16,
    zIndex: 1,
    "&>button": {
      padding: 0,
      minWidth: "auto",
      "&>svg": {
        color: theme.design.color.text.secondary,
        cursor: "pointer",
        transition: ".3s",
        width: theme.design.size.icon.lg,
        height: theme.design.size.icon.lg,
        "&:hover": {
          color: theme.design.color.text.primary,
          transition: ".3s",
        },
      },
    },
  },

  logoImg: {
    width: 36,
    height: 36,
  },

  titleText: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: theme.design.typography.size.titleSmall,
    fontWeight: 700,
    lineHeight: theme.design.typography.lineHeight.compact,
    color: theme.design.color.text.primary,
  },

  payCard: {
    display: "flex",
    flexDirection: "column",
    gap: theme.design.space.xs,
    width: "100%",
    padding: `${theme.design.space.sm}px ${theme.design.space.md}px`,
    borderRadius: theme.design.radius.md,
    backgroundColor: theme.design.color.surface.container,
    border: `1px solid ${theme.design.color.border.subtle}`,
  },

  fieldLabel: {
    fontFamily: theme.design.typography.fontFamily,
    fontSize: theme.design.typography.size.caption,
    fontWeight: 700,
    lineHeight: theme.design.typography.lineHeight.compact,
    color: theme.design.color.text.secondary,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },

  payRow: {
    display: "flex",
    width: "100%",
    alignItems: "stretch",
    gap: theme.design.space.sm,

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  sellInput: {
    flex: 1,
    minWidth: 0,

    "& .MuiInputBase-root": {
      ...controlSurface(theme),
      height: theme.design.size.control.md,
      borderRadius: theme.design.radius.md,
      fontFamily: theme.design.typography.fontFamily,
      fontWeight: 700,
    },

    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },

    "& .MuiInputBase-input": {
      padding: "8px 4px 8px 0",
      fontSize: theme.design.typography.size.body,
      fontWeight: 700,
      lineHeight: theme.design.typography.lineHeight.compact,
      color: theme.design.color.text.primary,
      "&::placeholder": {
        color: theme.design.color.text.secondary,
        opacity: 1,
      },
    },

    "& .MuiInputAdornment-root": {
      marginRight: theme.design.space.xs,
    },

    "& .MuiInputAdornment-root p": {
      fontFamily: theme.design.typography.fontFamily,
      fontWeight: 700,
      fontSize: theme.design.typography.size.body,
      color: theme.design.color.text.muted,
    },

    "& .MuiInputBase-root.Mui-focused": {
      backgroundColor: theme.design.color.surface.controlHover,
      boxShadow: theme.design.shadow.focus,
    },
  },

  swapTokenSelect: {
    minWidth: 132,
    flexShrink: 0,

    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
    },

    "& .MuiInputBase-root": {
      ...controlSurface(theme),
      height: theme.design.size.control.md,
      borderRadius: theme.design.radius.md,
      fontFamily: theme.design.typography.fontFamily,
      fontWeight: 700,
      fontSize: theme.design.typography.size.bodySmall,
      color: theme.design.color.text.primary,
    },

    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },

    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      padding: "6px 32px 6px 10px",
    },

    "& .MuiSelect-icon": {
      color: theme.design.color.text.muted,
      right: 8,
    },
  },

  tokenValue: {
    display: "flex",
    alignItems: "center",
    gap: theme.design.space.xs,
    fontFamily: theme.design.typography.fontFamily,
    fontSize: theme.design.typography.size.bodySmall,
    fontWeight: 700,
    color: theme.design.color.text.primary,
  },

  tokenPlaceholder: {
    color: theme.design.color.text.secondary,
    fontSize: theme.design.typography.size.bodySmall,
    fontWeight: 700,
  },

  menuPaper: {
    maxHeight: 320,
    marginTop: theme.design.space.xs,
    padding: theme.design.space.xs,
    minWidth: 260,
    border: `1px solid ${theme.design.color.border.popover}`,
    borderRadius: theme.design.radius.lg,
    background: `linear-gradient(180deg, ${theme.design.color.surface.interactive} 0%, ${theme.design.color.surface.popover} 100%)`,
    color: theme.design.color.text.primary,
    boxShadow: theme.design.shadow.dropdown,
    overflowY: "auto",
  },

  menuItem: {
    display: "flex",
    gap: theme.design.space.sm,
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "36px !important",
    padding: `${theme.design.space.xs}px ${theme.design.space.sm}px`,
    borderRadius: theme.design.radius.sm,
    color: theme.design.color.text.primary,
    fontFamily: theme.design.typography.fontFamily,
    fontSize: theme.design.typography.size.bodySmall,
    fontWeight: 700,

    "&.Mui-selected": {
      backgroundColor: theme.design.color.overlay.white10,
    },

    "&.Mui-selected:hover, &:hover": {
      backgroundColor: theme.design.color.surface.controlHover,
    },
  },

  tokenOption: {
    display: "flex",
    alignItems: "center",
    gap: theme.design.space.xs,
  },

  tokenBalance: {
    color: theme.design.color.text.secondary,
    fontSize: theme.design.typography.size.caption,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },

  metaRow: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.design.space.sm,
    color: theme.design.color.text.secondary,
    fontFamily: theme.design.typography.fontFamily,
    fontSize: theme.design.typography.size.caption,
    fontWeight: 700,
    lineHeight: theme.design.typography.lineHeight.compact,
  },

  maxButton: {
    minWidth: 44,
    padding: "0 8px",
    minHeight: 22,
    backgroundColor: theme.design.color.surface.control,
    color: theme.design.color.text.muted,
    fontSize: theme.design.typography.size.caption,
    fontWeight: 700,
    lineHeight: 1.4,

    "&:hover": {
      color: theme.design.color.text.primary,
      backgroundColor: theme.design.color.action.secondaryHover,
    },
  },

  activeButton: {
    color: theme.design.color.text.primary,
    backgroundColor: theme.design.color.action.secondaryHover,
  },

  receiveCard: {
    display: "flex",
    flexDirection: "column",
    gap: theme.design.space.xs,
    width: "100%",
    padding: `${theme.design.space.sm}px ${theme.design.space.md}px`,
    borderRadius: theme.design.radius.md,
    backgroundColor: theme.design.color.surface.container,
    border: `1px solid ${theme.design.color.border.subtle}`,
  },

  receiveValue: {
    display: "flex",
    alignItems: "center",
    gap: theme.design.space.xs,
    fontFamily: theme.design.typography.fontFamily,
    fontSize: theme.design.typography.size.body,
    fontWeight: 700,
    lineHeight: theme.design.typography.lineHeight.compact,
    color: theme.design.color.text.primary,
  },

  receiveIcon: {
    width: theme.design.size.icon.md,
    height: theme.design.size.icon.md,
  },

  confirmBut: {
    ...actionPrimary(theme),
    minWidth: 140,
    padding: "8px 20px",
    fontSize: theme.design.typography.size.bodySmall,
    fontWeight: 700,
    marginTop: theme.design.space.xs,
  },
}));

export default styles;
